import { useEffect, useState } from 'react';
import { TRACKS } from '../../constants/appConstants.js';
import { adminCohortMemory, adminCountDecisionTraces } from '../../services/adminClient.js';
import { T, card, sectionTitle, eyebrow, PageHeader, StatCard, Pill, Scaffold } from './ui.jsx';

const trackName = (id) => TRACKS[id]?.label || id || '—';

export default function AdminMemory() {
  const [buckets, setBuckets] = useState(null);
  const [traceCount, setTraceCount] = useState(null);

  useEffect(() => {
    let c = false;
    Promise.all([adminCohortMemory(), adminCountDecisionTraces()])
      .then(([b, tc]) => { if (c) return; setBuckets(b || []); setTraceCount(tc); })
      .catch(() => { if (!c) setBuckets([]); });
    return () => { c = true; };
  }, []);

  if (!buckets) return <p style={{ color: T.slate, fontSize: 13 }}>Loading M[Emory]…</p>;

  const active = buckets.filter(b => b.sample_size >= 8).length;
  const th = { textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: T.slate, padding: '0 10px 10px' };
  const td = { fontSize: 13, color: T.ink, padding: '10px', borderTop: `1px solid ${T.border}` };

  return (
    <div>
      <PageHeader title="M[Emory]" subtitle="The cross-learner moat — evidence surface. Shows only what's real." />

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        <StatCard label="Active Buckets" value={active} delta="N ≥ 8 (significant)" deltaColor={T.emerald} tint={T.emerald} />
        <StatCard label="Total Buckets" value={buckets.length} delta="concept × lesson" deltaColor={T.slate} tint={T.amber} />
        <StatCard label="Decision Traces" value={traceCount ?? '—'} delta="turns captured" deltaColor={T.slate} tint={T.amber} />
      </div>

      <div style={{ ...card, padding: '16px 18px', overflowX: 'auto', marginBottom: 16 }}>
        <p style={{ ...sectionTitle, marginBottom: 12 }}>Cohort Concept Buckets</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
          <thead><tr>
            <th style={th}>Concept</th><th style={th}>Track</th><th style={th}>n</th><th style={th}>Pass rate</th><th style={th}>Status</th>
          </tr></thead>
          <tbody>
            {buckets.map((b, i) => (
              <tr key={i}>
                <td style={{ ...td, fontWeight: 600 }}>{b.concept_key}</td>
                <td style={{ ...td, color: T.slate }}>{trackName(b.track_id)}</td>
                <td style={td}>{b.sample_size}</td>
                <td style={td}>{b.pass_rate != null ? `${Math.round(b.pass_rate * 100)}%` : '—'}</td>
                <td style={td}>{b.sample_size >= 8
                  ? <Pill text="Active" color={T.emerald} bg="rgba(5,150,105,0.12)" />
                  : <Pill text="Building" color={T.amber} bg="rgba(217,119,6,0.12)" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {buckets.length === 0 && (
          <p style={{ fontSize: 13, color: T.slate, margin: '14px 0 0', textAlign: 'center' }}>
            No buckets yet — they fill as learners study. Each reaches "Active" at N ≥ 8 distinct learners.
          </p>
        )}
      </div>

      <p style={{ ...eyebrow, margin: '0 0 10px' }}>Pending — unlocks as the loop matures</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px' }}>
          <Scaffold title="Remediation Effectiveness" body="Which repairs clear which gaps — available once the decision-trace ↔ recheck-outcome linkage is wired." />
        </div>
        <div style={{ flex: '1 1 300px' }}>
          <Scaffold title="Pass-Rate Delta (cohort-informed vs baseline)" body="The moat-evidence chart — unlocks when N ≥ 8 buckets reach statistical significance and the before/after cutover has data." />
        </div>
      </div>
    </div>
  );
}
